// DocumentApprovalButton.js — Approval action in document editor header
import React, { useState, useCallback, useMemo } from 'react';
import { ShieldCheck, ShieldAlert, ShieldX, Undo2 } from 'lucide-react';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import {
  isDocumentApproved,
  isModifiedAfterApproval,
  canApproveDocument,
  getUnreviewedSections,
  approveDocument,
  unapproveDocument,
} from '../../utils/approvalUtils';

const DocumentApprovalButton = ({
  documentData,
  setDocumentData,
  approvalConfig,
  currentSections,
  patientId,
  docId,
  userId,
  showSaveStatus,
  isViewOnly,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const approved = useMemo(() => isDocumentApproved(documentData), [documentData]);
  const modified = useMemo(() => isModifiedAfterApproval(documentData), [documentData]);
  const unreviewedSections = useMemo(
    () => getUnreviewedSections(documentData, approvalConfig, currentSections),
    [documentData, approvalConfig, currentSections]
  );
  const canApprove = useMemo(
    () => canApproveDocument(documentData, approvalConfig, currentSections),
    [documentData, approvalConfig, currentSections]
  );

  const handleApprove = useCallback(async () => {
    if (isProcessing || !canApprove) return;
    setIsProcessing(true);
    try {
      const updatePayload = await approveDocument(patientId, docId, userId, documentData);
      setDocumentData(prev => ({ ...prev, ...updatePayload, approvedAt: new Date() }));
      showSaveStatus?.('saved');
    } catch (error) {
      console.error('[DocumentApprovalButton] Error approving document:', error);
      showSaveStatus?.('error');
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, canApprove, patientId, docId, userId, documentData, setDocumentData, showSaveStatus]);

  const handleUnapprove = useCallback(async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const updatePayload = await unapproveDocument(patientId, docId, userId, documentData);
      setDocumentData(prev => ({ ...prev, ...updatePayload }));
      showSaveStatus?.('saved');
    } catch (error) {
      console.error('[DocumentApprovalButton] Error unapproving document:', error);
      showSaveStatus?.('error');
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, patientId, docId, userId, documentData, setDocumentData, showSaveStatus]);

  // Don't render for view-only or if approval feature is disabled
  if (isViewOnly || !approvalConfig?.enabled) return null;

  // Don't show if document isn't at least completed
  const validStatuses = ['completed', 'approved', 'exported'];
  if (!documentData || !validStatuses.includes(documentData.status)) return null;

  // Approved state — show approved indicator with unapprove option
  if (approved) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {modified ? (
          <Tooltip title="Document was modified after approval" arrow>
            <Chip
              icon={<ShieldAlert size={14} />}
              label="Modified"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontSize: 'var(--text-xs)', height: '24px' }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Document is approved" arrow>
            <Chip
              icon={<ShieldCheck size={14} />}
              label="Approved"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: 'var(--text-xs)', height: '24px' }}
            />
          </Tooltip>
        )}
        <Tooltip title="Unapprove document" arrow>
          <button
            className="header-button"
            onClick={handleUnapprove}
            disabled={isProcessing}
            aria-label="Unapprove document"
            style={{ padding: '2px 6px', minWidth: 'auto' }}
          >
            <Undo2 size={14} />
          </button>
        </Tooltip>
      </div>
    );
  }

  // Not approved — show approve button with review progress
  const totalRequired = approvalConfig.requiredReviewSections?.length || 0;
  const reviewed = totalRequired - unreviewedSections.length;

  const tooltipText = canApprove
    ? 'Approve document'
    : `Review ${unreviewedSections.length} more section${unreviewedSections.length !== 1 ? 's' : ''} to approve`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <Tooltip title={tooltipText} arrow>
        <span>
          <div
            className={`header-button${!canApprove ? ' disabled' : ''}`}
            onClick={canApprove ? handleApprove : undefined}
            role="button"
            tabIndex={canApprove ? 0 : -1}
            aria-label={tooltipText}
            aria-disabled={!canApprove}
            style={!canApprove ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          >
            <ShieldCheck className="header-icon" size={18} />
            Approve
          </div>
        </span>
      </Tooltip>
      {totalRequired > 0 && (
        <Tooltip title={`${reviewed}/${totalRequired} required sections reviewed`} arrow>
          <Chip
            label={`${reviewed}/${totalRequired}`}
            size="small"
            color={canApprove ? 'success' : 'default'}
            variant="outlined"
            sx={{ fontSize: 'var(--text-xs)', height: '20px', minWidth: '36px' }}
          />
        </Tooltip>
      )}
    </div>
  );
};

export default React.memo(DocumentApprovalButton);
